import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Video as VideoIcon, Clock, Trash2, Play, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyVideosPage = ({ user }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchVideos();
    
    // Auto-refresh every 5 seconds if any video is still processing
    const interval = setInterval(() => {
      if (videos.some(v => v.status === 'uploaded' || v.status === 'processing')) {
        fetchVideos();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [videos.length]);

  const fetchVideos = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.get(`${API}/videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (videoId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      const token = localStorage.getItem('kaplie_token');
      await axios.delete(`${API}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success('Video deleted');
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handlePlayVideo = (videoId) => {
    navigate(`/video/${videoId}`);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'Processing...';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      uploaded: { text: 'Processing...', color: 'bg-yellow-500/10 text-yellow-500' },
      processing: { text: 'Generating Captions...', color: 'bg-violet-500/10 text-violet-400' },
      completed: { text: 'Ready', color: 'bg-[#22C55E]/10 text-[#22C55E]' },
      failed: { text: 'Failed', color: 'bg-red-500/10 text-red-500' }
    };
    const badge = badges[status] || badges.uploaded;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-black/40 text-white">
      {/* Navigation */}
      <nav className="border-b border-white/[0.08] bg-white/[0.03]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold tracking-tighter">Kaplie</div>
          <Link to="/dashboard" data-testid="back-to-dashboard-link">
            <button className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-5xl font-bold tracking-tighter mb-2" data-testid="my-videos-heading">
              My Videos
            </h1>
            <p className="text-lg text-[#A1A1AA]">
              {videos.length} {videos.length === 1 ? 'video' : 'videos'}
            </p>
          </div>
          <Link to="/upload-video" data-testid="upload-video-link">
            <button className="px-6 py-3 font-bold bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all">
              Upload Video
            </button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-white/[0.03] rounded-full flex items-center justify-center mx-auto mb-6">
              <VideoIcon className="w-10 h-10 text-[#A1A1AA]" />
            </div>
            <h2 className="text-2xl font-bold mb-2">No videos yet</h2>
            <p className="text-[#A1A1AA] mb-8">Upload your first video to get started</p>
            <Link to="/upload-video">
              <button className="px-6 py-3 font-bold bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all">
                Upload Video
              </button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div
                key={video.id}
                onClick={() => video.status === 'completed' && handlePlayVideo(video.id)}
                className={`bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden transition-all ${
                  video.status === 'completed'
                    ? 'hover:border-[#D4AF37]/50 cursor-pointer'
                    : 'opacity-75'
                }`}
                data-testid={`video-card-${video.id}`}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-black/40 flex items-center justify-center relative group">
                  <VideoIcon className="w-12 h-12 text-[#A1A1AA]" />
                  {video.status === 'completed' && (
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Play className="w-12 h-12 text-[#D4AF37]" />
                    </div>
                  )}
                  {video.status === 'processing' && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader className="w-8 h-8 text-[#D4AF37] animate-spin" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-bold text-lg truncate flex-1" title={video.filename}>
                      {video.filename}
                    </h3>
                    <button
                      onClick={(e) => handleDelete(video.id, e)}
                      className="p-2 hover:bg-[#1E1E24] rounded-lg transition-colors text-[#A1A1AA] hover:text-[#FF3366]"
                      data-testid={`delete-video-${video.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-[#A1A1AA] mb-3">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDuration(video.duration)}
                    </div>
                    <span>•</span>
                    <span>{formatDate(video.created_at)}</span>
                    {video.detected_language && (
                      <>
                        <span>•</span>
                        <span className="text-[#D4AF37] font-medium">{video.detected_language.toUpperCase()}</span>
                      </>
                    )}
                  </div>

                  {getStatusBadge(video.status)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyVideosPage;
